import { create } from 'zustand';
import type { ChatMessage } from '@the-wall/shared';

interface ChatState {
  messages: ChatMessage[];
  isModalOpen: boolean;
  hasUnread: boolean;
  replyingTo: ChatMessage | null;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;
  updateUserAppearance: (
    userId: string,
    appearance: {
      usernameColor?: string | null;
      usernameEffect?: ChatMessage['usernameEffect'] | null;
    }
  ) => void;
  setReplyingTo: (message: ChatMessage | null) => void;
  clearReplyingTo: () => void;
  removeMessages: (messageIds: string[]) => void;
  setModalOpen: (open: boolean) => void;
  setHasUnread: (value: boolean) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  isModalOpen: false,
  hasUnread: false,
  replyingTo: null,

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
      hasUnread: state.isModalOpen ? state.hasUnread : true,
    })),

  updateUserAppearance: (userId, appearance) =>
    set((state) => ({
      messages: state.messages.map((message) =>
        message.userId !== userId
          ? message
          : {
              ...message,
              ...(appearance.usernameColor !== undefined
                ? { usernameColor: appearance.usernameColor }
                : {}),
              ...(appearance.usernameEffect !== undefined
                ? { usernameEffect: appearance.usernameEffect }
                : {}),
              ...(message.replyTo?.userId === userId
                ? {
                    replyTo: {
                      ...message.replyTo,
                      ...(appearance.usernameColor !== undefined
                        ? { usernameColor: appearance.usernameColor }
                        : {}),
                      ...(appearance.usernameEffect !== undefined
                        ? { usernameEffect: appearance.usernameEffect }
                        : {}),
                    },
                  }
                : {}),
            }
      ),
      replyingTo:
        state.replyingTo?.userId === userId
          ? {
              ...state.replyingTo,
              ...(appearance.usernameColor !== undefined
                ? { usernameColor: appearance.usernameColor }
                : {}),
              ...(appearance.usernameEffect !== undefined
                ? { usernameEffect: appearance.usernameEffect }
                : {}),
            }
          : state.replyingTo,
    })),

  setReplyingTo: (message) => set({ replyingTo: message }),

  clearReplyingTo: () => set({ replyingTo: null }),

  removeMessages: (messageIds) =>
    set((state) => ({
      messages: state.messages.filter((m) => !messageIds.includes(m.id)),
      replyingTo:
        state.replyingTo && messageIds.includes(state.replyingTo.id) ? null : state.replyingTo,
    })),

  setModalOpen: (open) =>
    set((state) => ({
      isModalOpen: open,
      hasUnread: open ? false : state.hasUnread,
    })),

  setHasUnread: (value) => set({ hasUnread: value }),
}));
